import { useCallback, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { useMutation } from '@apollo/client/react';

import { EscrowPartyTypeEnum, ContactTypeEnum, AgreementFormEnum } from 'src/graphql/schema';
import { SuggestedChangeCreateDocument } from 'src/graphql/actions/suggestChanges/suggestedChangeCreate.mutation';
import { usePartySuggestChangesSlice } from 'src/slices';
import { suggestChangesVar, writeNewSuggestChanges } from 'src/graphql/client/cache';

import { createRequiredContext } from '../createRequiredContext';
import { useSoftwareEscrow } from '../software-escrow';

import type { ApolloClient } from '@apollo/client';
import type { SuggestedChangeStatusEnum } from 'src/graphql/schema';
import type { ISuggestedChangeCreateMutation } from 'src/graphql/actions/suggestChanges/suggestedChangeCreate.mutation';
import type { IRepresentativeForm } from 'src/components/Escrow/Representative';
import type { IRepresentative } from 'src/containers/Escrow/Representatives';

const [usePartySuggestChanges, PartySuggestChangesProvider] = createRequiredContext<
  {
    isSendSuggestionsEnabled: boolean;
    partyType: EscrowPartyTypeEnum;
    agreementType: string;
    undoRepresentative: (id: string) => void;
    createSuggestedChanges: () => Promise<ApolloClient.MutateResult<ISuggestedChangeCreateMutation>>;
    editRepresentative: (id: string, representative: Partial<IRepresentativeForm>) => void;
    canSuggestActivationNotification: boolean;
    suggestedChanges: {
      organization: Record<string, unknown>;
      activationNotification: Record<string, unknown>;
      editedContacts: IRepresentative[];
      newContacts: IRepresentative[];
      deletedContacts: IRepresentative[];
    };
  } & Omit<ReturnType<typeof usePartySuggestChangesSlice>, 'setCurrentData' | 'undoRepresentative'>
>();

type Props = {
  partyType: EscrowPartyTypeEnum;
};

const PartySuggestChangesContextProvider = ({ partyType, children }: React.PropsWithChildren<Props>) => {
  const {
    softwareEscrow,
    softwareEscrow: { id: escrowId, agreement, activationNotification },
  } = useSoftwareEscrow();
  const {
    undoRepresentative,
    editRepresentative,
    organization: suggestedOrganization,
    representatives: suggestedRepresentatives,
    activationNotification: suggestedActivationNotification,
    setCurrentData,
    ...rest
  } = usePartySuggestChangesSlice();

  const partyContacts = softwareEscrow[partyType]?.contacts ?? [];
  const representatives =
    partyContacts.map(({ id, name, email, phone, signatory }) => ({
      id,
      name,
      email,
      phone: phone || '',
      signatory,
    })) ?? [];

  useEffect(() => setCurrentData({ representatives, activationNotification }), []);

  const onUndoRepresentative = useCallback(
    (id: string) => {
      const representative = partyContacts.find((rep) => rep.id === id);

      if (!representative) return;

      undoRepresentative({
        id,
        name: representative.name,
        email: representative.email,
        phone: representative.phone ?? '',
        signatory: representative.signatory,
      });
    },
    [partyContacts, undoRepresentative],
  );

  const onEditRepresentative = useCallback(
    (id: string, representative: Partial<IRepresentativeForm>) => {
      const currentRepresentative = representatives.find((rep) => rep.id === id);

      if (!currentRepresentative) return editRepresentative(id, representative);

      // Compare old and new values to detect changes
      const changedValues = filterNullValues({
        name: getSuggestedChangeValue(representative.name, currentRepresentative.name),
        email: getSuggestedChangeValue(representative.email, currentRepresentative.email),
        phone: getSuggestedChangeValue(representative.phone, currentRepresentative.phone),
        signatory: getSuggestedChangeValue(Boolean(representative.signatory), Boolean(currentRepresentative.signatory)),
      });

      // Only mark as edit if values actually changed
      const suggestType = isEmpty(changedValues) ? undefined : 'edit';
      editRepresentative(id, representative, suggestType);
    },
    [representatives, editRepresentative],
  );

  const [createSuggestedChange] = useMutation(SuggestedChangeCreateDocument, {
    onCompleted: (data) => {
      if (!data.suggestedChangeCreate?.success) return;

      const suggestedChange = data.suggestedChangeCreate.suggestedChange;

      if (!suggestedChange) return;

      writeNewSuggestChanges(suggestChangesVar)(partyType, {
        id: suggestedChange.id,
        creator: suggestedChange.creatorType,
        status: suggestedChange.partyStatus as SuggestedChangeStatusEnum,
        payload: suggestedChange.payload,
        receiver: suggestedChange.receiverType as EscrowPartyTypeEnum,
      });
    },
  });

  const isBipartiteBeneficiary =
    agreement?.agreementForm === AgreementFormEnum.Bipartite && partyType === EscrowPartyTypeEnum.Beneficiary;

  const organizationModifiedFields = filterNullValues({
    companyName: getSuggestedChangeValue(
      suggestedOrganization?.companyName,
      softwareEscrow[partyType]?.companyName ?? '',
    ),
    companyRegistrationNumber: getSuggestedChangeValue(
      suggestedOrganization?.companyRegistration,
      softwareEscrow[partyType]?.companyRegistrationNumber ?? '',
    ),
    companyWebsite: getSuggestedChangeValue(
      suggestedOrganization?.companyWebsite,
      softwareEscrow[partyType]?.companyWebsite ?? '',
    ),
    country: getSuggestedChangeValue(suggestedOrganization?.country, softwareEscrow[partyType]?.country ?? ''),
    region: getSuggestedChangeValue(suggestedOrganization?.region, softwareEscrow[partyType]?.region ?? ''),
    city: getSuggestedChangeValue(suggestedOrganization?.city, softwareEscrow[partyType]?.city ?? ''),
    postalCode: getSuggestedChangeValue(suggestedOrganization?.postalCode, softwareEscrow[partyType]?.postalCode ?? ''),
    street: getSuggestedChangeValue(suggestedOrganization?.street, softwareEscrow[partyType]?.street ?? ''),
    streetNumber: getSuggestedChangeValue(
      suggestedOrganization?.streetNumber,
      softwareEscrow[partyType]?.streetNumber ?? '',
    ),
  });

  const activationNotificationModifiedFields = filterNullValues({
    activationNotification: getSuggestedChangeValue(Boolean(suggestedActivationNotification), activationNotification),
  });

  const newContacts = suggestedRepresentatives.filter((rep) => rep.id.includes('new-'));
  const editedContacts = suggestedRepresentatives.filter(({ suggestType }) => suggestType === 'edit');
  const deletedContacts = suggestedRepresentatives.filter(({ suggestType }) => suggestType === 'remove');

  const createSuggestedChanges = () => {
    const newContactsMap = newContacts.map(({ id, suggestType, ...rest }) => ({
      ...rest,
      contactType: ContactTypeEnum.Administrative,
    }));
    const editedContactsMap = editedContacts.map(({ suggestType, ...rest }) => rest);
    const deletedContactsIds = deletedContacts.map(({ id }) => ({ id }));
    const suggestedData = {
      ...organizationModifiedFields,
      ...(editedContactsMap.length && { editedContacts: editedContactsMap }),
      ...(newContactsMap.length && { newContacts: newContactsMap }),
      ...(deletedContactsIds.length && { deletedContacts: deletedContactsIds }),
      ...(isBipartiteBeneficiary && activationNotificationModifiedFields),
    };
    const partyParamKey = `${partyType}SuggestedChange`;

    return createSuggestedChange({
      variables: {
        escrowId,
        suggestedChangeParams: {
          [partyParamKey]: suggestedData,
        },
      },
    });
  };

  const activeRepresentatives = suggestedRepresentatives.filter(
    (representative) => representative.suggestType !== 'remove',
  );

  const isSignatoryMissing =
    activeRepresentatives.length > 0 && !activeRepresentatives.some(({ signatory }) => signatory);

  const isSendSuggestionsEnabled =
    [
      !isEmpty(organizationModifiedFields),
      !isEmpty(activationNotificationModifiedFields),
      editedContacts.length,
      newContacts.length,
      deletedContacts.length,
    ].some(Boolean) && !(isSignatoryMissing && !isBipartiteBeneficiary);

  const value = {
    ...rest,
    isSendSuggestionsEnabled,
    organization: suggestedOrganization,
    representatives: suggestedRepresentatives,
    partyType,
    agreementType: agreement?.agreementForm || '',
    undoRepresentative: onUndoRepresentative,
    editRepresentative: onEditRepresentative,
    createSuggestedChanges,
    canSuggestActivationNotification: isBipartiteBeneficiary,
    activationNotification: suggestedActivationNotification,
    suggestedChanges: {
      organization: organizationModifiedFields,
      activationNotification: activationNotificationModifiedFields,
      editedContacts,
      newContacts,
      deletedContacts,
    },
  };

  return <PartySuggestChangesProvider value={value}>{children}</PartySuggestChangesProvider>;
};

export { usePartySuggestChanges, PartySuggestChangesContextProvider };

function getSuggestedChangeValue(
  suggestedChangeValue: string | boolean | undefined,
  currentValue: string | boolean | undefined,
): string | boolean | null {
  if (currentValue && suggestedChangeValue === '') return '';

  if (suggestedChangeValue !== currentValue) return suggestedChangeValue ?? null;

  return null;
}

function filterNullValues(obj: Record<string, unknown>) {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== null));
}
